// Posting one approved expense report to the accounting system.
//
// The point of the preview is that a configuration problem never reaches
// Finance as "QuickBooks failed". An unmapped category and an unreachable
// ledger are different problems with different remedies, and the first is
// fixable in a screen we can name — so the card names it.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, ExternalLink, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { getApiErrorMessage } from '@/lib/apiError';
import { UnknownPostingResolver } from '@/features/payroll/components/UnknownPostingResolver';
import { useHasPermission } from '@/rbac/useRbac';
import {
  previewExpensePosting,
  isOutcomeUnknown,
  postExpenseReport,
  previewExpenseSettlement,
  postExpenseSettlement,
  type ExpenseDraft,
  type PostingIssue,
  type AccountingProvider,
} from '@/services/accountingService';

/** Where a missing mapping is fixed. Named so the message is actionable — "unable
 *  to post" sends somebody hunting. */
const MAPPING_SCREEN = 'Payroll → Finance → Accounting';

function remedyFor(i: PostingIssue, t: (k: string, o?: Record<string, unknown>) => string): string {
  switch (i.code) {
    case 'ACCOUNT_MAPPING_MISSING':
    case 'CLASS_MAPPING_MISSING':
    case 'LOCATION_MAPPING_MISSING':
      return t('accounting.fixInMapping', {
        defaultValue: 'Configure it in {{screen}}.',
        screen: MAPPING_SCREEN,
      });
    case 'ACCOUNTING_CONNECTION_MISSING':
    case 'ACCOUNTING_CONNECTION_UNASSIGNED':
    case 'ACCOUNTING_CONNECTION_INACTIVE':
      return t('accounting.fixConnection', {
        defaultValue: "Connect this company's accounting system in {{screen}}.",
        screen: MAPPING_SCREEN,
      });
    case 'ACCOUNTING_CONFIG_MISSING':
      // A different screen section from a missing mapping, and a different
      // job: the ledger can be connected and every account mapped with this
      // still unanswered.
      return t('accounting.fixPolicy', {
        defaultValue: "Choose this company's accounting policy in {{screen}}.",
        screen: MAPPING_SCREEN,
      });
    case 'DOC_DATE_UNAVAILABLE':
      // The policy is right and the event has not happened. Saying "fix the
      // mapping" here would send them somewhere that cannot help.
      return t('accounting.fixDate', {
        defaultValue: 'Either wait for that to happen, or change the date rule in {{screen}}.',
        screen: MAPPING_SCREEN,
      });
    default:
      return '';
  }
}

/**
 * The CASH leg, as its own row.
 *
 * Recognition says the company owes the employee; this says it has been paid.
 * Two events, two dates, two postings — a company can legitimately have done
 * one and not the other, so this is pressed separately.
 *
 * It renders NOTHING when there is no cash leg to post, which is the common
 * case and not a problem: the reimbursement may not be paid yet, or may have
 * been paid through payroll, where that run's journal already cleared the
 * payable. The server says which; none of those are worth pushing at somebody
 * reading an expense report.
 */
/** The ledger product's name, for sentences like "check QuickBooks". */
const ledgerName = (p: AccountingProvider) => (p === 'quickbooks' ? 'QuickBooks' : 'your accounting system');

/** A refused post, kept on screen. The toast carries the server's words and fades; a
 *  refusal — above all "this report belongs to a company outside your accounting
 *  access" — must still be readable once it has, or a refused post reads as a no-op. */
export interface PostingRefusal {
  title: string;
  /** The server's own explanation. */
  detail: string;
}

/** The HTTP status of a failed request, when one came back. */
const statusOf = (e: unknown) => (e as { response?: { status?: number } })?.response?.status;

/** Company reach (403): the report's company is outside the caller's accounting access.
 *  Not "not set up" — so the card must say so rather than quietly disappear. */
export const isOutOfReach = (e: unknown) => statusOf(e) === 403;

const RefusalNotice: React.FC<{
  refusal: PostingRefusal | null;
  onDismiss?: () => void;
  testId: string;
}> = ({ refusal, onDismiss, testId }) => {
  if (!refusal) return null;
  return (
    <div role="alert" data-testid={testId}
      className="flex items-start gap-2 rounded-md bg-danger-soft p-3 text-xs text-danger-strong">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1 space-y-0.5">
        <p className="font-medium">{refusal.title}</p>
        {refusal.detail && refusal.detail !== refusal.title && <p>{refusal.detail}</p>}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss"
          className="rounded p-0.5 hover:bg-background/40">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

const SettlementRow: React.FC<{
  reportId: string;
  provider: AccountingProvider;
  canPost: boolean;
}> = ({ reportId, provider, canPost }) => {
  const { t } = useTranslation(['expenses']);
  const [draft, setDraft] = useState<ExpenseDraft | null>(null);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState<string | null>(null);
  const [unknown, setUnknown] = useState(false); // D10: resolve, never retry
  const [refusal, setRefusal] = useState<PostingRefusal | null>(null);

  const load = useCallback(() => {
    previewExpenseSettlement(provider, reportId)
      .then(setDraft)
      .catch(() => setDraft(null));
  }, [provider, reportId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    setPosting(true);
    setRefusal(null);
    try {
      const out = await postExpenseSettlement(provider, reportId);
      setPosted(out.posting?.external_doc_number || out.posting?.external_doc_id || '');
      toast.success(out.already_posted
        ? t('accounting.alreadyPosted', { defaultValue: 'Already posted — nothing was sent again.' })
        : t('accounting.settlementPosted', { defaultValue: 'Payment posted to accounting' }));
      load();
    } catch (e) {
      const fallback = t('accounting.settlementPostFailed', {
        defaultValue: 'Could not post the payment to accounting',
      });
      if (isOutcomeUnknown(e)) setUnknown(true);
      // The resolver speaks for an unknown outcome; every other failure stays on screen.
      else setRefusal({
        title: t('accounting.settlementNotPosted', { defaultValue: 'The payment was not posted' }),
        detail: getApiErrorMessage(e, fallback),
      });
      toast.error(getApiErrorMessage(e, fallback));
    } finally {
      setPosting(false);
    }
  };

  if (!draft) return null;
  const blockers = draft.issues.filter((i) => i.blocking);

  return (
    <div className="rounded-lg border border-border px-3 py-2 space-y-1" data-testid="accounting-settlement">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] text-foreground">
            {t('accounting.settlementTitle', { defaultValue: 'Payment to the employee' })}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {t('accounting.settlementSub', {
              defaultValue: 'Clears what was owed · {{date}}', date: draft.doc_date,
            })}
          </div>
        </div>
        {canPost && draft.ready && posted === null && !unknown && (
          <Button size="sm" variant="outline" onClick={post} disabled={posting}
            data-testid="accounting-post-settlement">
            {posting && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            {t('accounting.postSettlement', { defaultValue: 'Post the payment' })}
          </Button>
        )}
      </div>
      <RefusalNotice refusal={refusal} onDismiss={() => setRefusal(null)} testId="accounting-settlement-refused" />
      {unknown && (
        <UnknownPostingResolver
          provider={provider} legalEntityId={draft.legal_entity_id} kind="expense_settlement" sourceId={reportId}
          systemName={ledgerName(provider)} canResolve={canPost}
          onResolved={(p) => {
            setUnknown(false);
            if (p.status === 'posted') setPosted(p.external_doc_number || p.external_doc_id || '');
          }}
        />
      )}
      {posted !== null && (
        <div className="flex items-center gap-2 text-[13px] text-success">
          <ExternalLink className="w-3.5 h-3.5" />
          {posted
            ? t('accounting.postedAs', { defaultValue: 'Posted as {{ref}}', ref: posted })
            : t('accounting.postedNoRef', { defaultValue: 'Posted' })}
        </div>
      )}
      {blockers.map((i, n) => (
        <div key={`${i.code}-${n}`} className="text-[11px] text-danger">
          {i.message}
          {remedyFor(i, t as never) ? ` ${remedyFor(i, t as never)}` : ''}
        </div>
      ))}
    </div>
  );
};

export const ExpenseAccountingCard: React.FC<{
  reportId: string;
  /** Only an approved (or later) report can be recognised in the ledger. */
  reportStatus: string;
  provider?: AccountingProvider;
}> = ({ reportId, reportStatus, provider = 'quickbooks' }) => {
  const { t } = useTranslation(['expenses']);

  const [draft, setDraft] = useState<ExpenseDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState<string | null>(null);
  const [unknown, setUnknown] = useState(false); // D10: resolve, never retry
  const [refusal, setRefusal] = useState<PostingRefusal | null>(null);
  /** The preview itself was refused for company reach — say so, don't vanish. */
  const [previewRefusal, setPreviewRefusal] = useState<string | null>(null);

  const canSee = useHasPermission('accounting:read');
  const canPost = useHasPermission('accounting:post');
  // APPROVED **or later**: SCHEDULED and REIMBURSED are downstream of
  // approval, so cash can move before anybody posts. Tying this to payment
  // would leave a reimbursed expense permanently unrecognised.
  const postable = ['APPROVED', 'SCHEDULED', 'PAYMENT_FAILED', 'REIMBURSED'].includes(reportStatus);

  const load = useCallback(() => {
    if (!canSee || !postable) {
      setLoading(false);
      return;
    }
    setLoading(true);
    previewExpensePosting(provider, reportId)
      .then((d) => { setDraft(d); setPreviewRefusal(null); })
      // An accounting system that is not set up must not break the report
      // screen it is embedded in. The card simply does not render — unless the
      // refusal is about WHO is asking (company reach), which a vanished card
      // would hide: then the reason stays on screen.
      .catch((e) => {
        setDraft(null);
        // '' = refused, with no words from the server (the fallback is chosen at render).
        setPreviewRefusal(isOutOfReach(e) ? getApiErrorMessage(e, '') : null);
      })
      .finally(() => setLoading(false));
  }, [canSee, postable, provider, reportId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    setPosting(true);
    setRefusal(null);
    try {
      const out = await postExpenseReport(provider, reportId);
      const ref = out.posting?.external_doc_number || out.posting?.external_doc_id || '';
      setPosted(ref);
      if (out.already_posted) {
        toast.success(t('accounting.alreadyPosted', { defaultValue: 'Already posted — nothing was sent again.' }));
      } else {
        toast.success(t('accounting.posted', { defaultValue: 'Posted to accounting' }));
      }
      load();
    } catch (e) {
      // The server names the problem ("Arusha is not mapped to a location
      // yet") — show that, not ours.
      const fallback = t('accounting.postFailed', { defaultValue: 'Could not post to accounting' });
      if (isOutcomeUnknown(e)) setUnknown(true);
      // The resolver speaks for an unknown outcome; every other failure — a
      // company outside the caller's reach above all — stays on screen after
      // the toast has gone.
      else setRefusal({
        title: t('accounting.notPosted', { defaultValue: 'Not posted to accounting' }),
        detail: getApiErrorMessage(e, fallback),
      });
      toast.error(getApiErrorMessage(e, fallback));
    } finally {
      setPosting(false);
    }
  };

  if (!canSee || !postable) return null;
  if (loading) {
    return (
      <Card><CardContent className="py-5 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('accounting.checking', { defaultValue: 'Checking the accounting system…' })}
      </CardContent></Card>
    );
  }
  if (!draft) {
    if (previewRefusal === null) return null;
    return (
      <Card data-testid="expense-accounting-card"><CardContent className="py-5 space-y-3">
        <h4 className="text-sm font-semibold text-foreground">
          {t('accounting.title', { defaultValue: 'Accounting' })}
        </h4>
        <RefusalNotice testId="accounting-preview-refused" refusal={{
          title: t('accounting.cannotPost', { defaultValue: 'You cannot preview or post this report' }),
          detail: previewRefusal || t('accounting.outOfReach', {
            defaultValue: 'This report belongs to a company outside your accounting access.',
          }),
        }} />
      </CardContent></Card>
    );
  }

  const blockers = draft.issues.filter((i) => i.blocking);

  return (
    <Card data-testid="expense-accounting-card">
      <CardContent className="py-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-foreground">
                {t('accounting.title', { defaultValue: 'Accounting' })}
              </h4>
              {draft.ready ? (
                <Badge variant="outline" className="text-success border-success/40" data-testid="accounting-ready">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {t('accounting.readyToPost', { defaultValue: 'Ready to post' })}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-danger border-danger/40" data-testid="accounting-blocked">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {t('accounting.blocked', { defaultValue: 'Blocked' })}
                </Badge>
              )}
            </div>
            {/* The destination, before the numbers: Finance needs to see whose
                books this lands in before pressing Post. */}
            <div className="mt-1 text-xs text-muted-foreground">
              {draft.company_name
                ? t('accounting.destination', {
                    defaultValue: 'To {{company}} · {{date}} · {{currency}}',
                    company: draft.company_name, date: draft.doc_date, currency: draft.currency,
                  })
                : `${draft.doc_date} · ${draft.currency}`}
            </div>
          </div>
          {canPost && draft.ready && !unknown && (
            <Button size="sm" onClick={post} disabled={posting} data-testid="accounting-post">
              {posting && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              {t('accounting.post', { defaultValue: 'Post to accounting' })}
            </Button>
          )}
        </div>

        <RefusalNotice refusal={refusal} onDismiss={() => setRefusal(null)} testId="accounting-post-refused" />
        {unknown && (
          <UnknownPostingResolver
            provider={provider} legalEntityId={draft.legal_entity_id} kind="expense_report" sourceId={reportId}
            systemName={ledgerName(provider)} canResolve={canPost}
            onResolved={(p) => {
              setUnknown(false);
              if (p.status === 'posted') setPosted(p.external_doc_number || p.external_doc_id || '');
            }}
          />
        )}
        {posted !== null && (
          <div className="flex items-center gap-2 text-sm text-success" data-testid="accounting-posted-ref">
            <ExternalLink className="w-3.5 h-3.5" />
            {posted
              ? t('accounting.postedAs', { defaultValue: 'Posted as {{ref}}', ref: posted })
              : t('accounting.postedNoRef', { defaultValue: 'Posted' })}
          </div>
        )}

        {/* Every problem at once, each with where to fix it — the person is
            about to open the mapping screen and should only go there once. */}
        {blockers.length > 0 && (
          <ul className="space-y-2" data-testid="accounting-issues">
            {blockers.map((i, n) => (
              <li key={`${i.code}-${i.line ?? 0}-${n}`} className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
                <div className="text-[13px] text-foreground">
                  {i.line ? (
                    <span className="text-muted-foreground mr-1">
                      {t('accounting.itemN', { defaultValue: 'Item {{n}}:', n: i.line })}
                    </span>
                  ) : null}
                  {i.message}
                </div>
                {remedyFor(i, t as never) && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">{remedyFor(i, t as never)}</div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* The cash leg, when there is one to post. Hides itself otherwise —
            most reports have nothing to settle yet, and one paid through
            payroll was already cleared by that run's journal. */}
        <SettlementRow reportId={reportId} provider={provider} canPost={canPost} />

        {/* What would be sent, line by line, with each line's own class and
            location — two items in different places resolve differently. */}
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-3 font-medium">{t('accounting.account', { defaultValue: 'Account' })}</th>
                <th className="py-1.5 pr-3 font-medium">{t('accounting.class', { defaultValue: 'Class' })}</th>
                <th className="py-1.5 pr-3 font-medium">{t('accounting.location', { defaultValue: 'Location' })}</th>
                <th className="py-1.5 font-medium text-right">{t('accounting.amount', { defaultValue: 'Amount' })}</th>
              </tr>
            </thead>
            <tbody>
              {draft.lines.map((l) => (
                <tr key={l.line} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-3">
                    <span className={l.external_account_id ? '' : 'text-danger'}>
                      {l.account_name || l.logical_code}
                    </span>
                    <span className="text-muted-foreground ml-1">{l.dr_cr}</span>
                  </td>
                  <td className="py-1.5 pr-3"><DimensionCell o={l.class} /></td>
                  <td className="py-1.5 pr-3"><DimensionCell o={l.location} /></td>
                  <td className="py-1.5 text-right tabular-nums">{l.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Recognition is the full cost; an advance is cash already paid. Shown
            so Finance can see how the claim was funded, and never subtracted. */}
        {draft.advance_covered && (
          <div className="text-[11px] text-muted-foreground" data-testid="accounting-advance">
            {t('accounting.advanceNote', {
              defaultValue:
                'An advance already covered {{amount}} of this claim. The expense is recognised in full; the advance settles separately.',
              amount: draft.advance_covered,
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/** A dimension reads as its value, as "—" when none applies, or as the thing
 *  that needs binding when it is missing. Those must look different. */
const DimensionCell: React.FC<{ o: { status: string; external_value_name?: string; external_value_id?: string; source_label?: string } }> = ({ o }) => {
  if (o.status === 'resolved') {
    return <span>{o.external_value_name || o.external_value_id}</span>;
  }
  if (o.status === 'unmapped') {
    return <span className="text-danger">{o.source_label || '—'}</span>;
  }
  return <span className="text-muted-foreground">—</span>;
};

export default ExpenseAccountingCard;
